import type { ApolloQueryResult } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { GET_GIT_ROOTS, GET_GIT_ROOTS_FRAGMENT } from "../../queries";
import type { IGitRootAttr } from "../../types";
import { getGitRootsFromResult } from "../utils";
import { getFragmentData } from "gql/fragment-masking";
import type {
  CloningStatus,
  GetGitRootsQuery,
  GetGitRootsViewFragmentFragment,
  GitRootsFiltersInput,
  GitRootsSortInput,
  ResourceState,
} from "gql/graphql";
import { Logger } from "utils/logger";

interface IUseGitRootsDataQuery {
  gitRoots: IGitRootAttr[];
  handleNextPage: () => Promise<void>;
  total: number;
  loadingRoots: boolean;
  rootsRefetch: () => Promise<ApolloQueryResult<GetGitRootsQuery>>;
}

const useGitRootsDataQuery = (
  groupName: string,
  filters: GitRootsFiltersInput,
  first: number,
  search: string,
  sortBy: GitRootsSortInput[],
): IUseGitRootsDataQuery => {
  const { branches, cloningStatuses, includesHealthCheck, nicknames, state } =
    filters;

  const {
    data,
    loading: loadingRoots,
    refetch: rootsRefetch,
    fetchMore,
  } = useQuery(GET_GIT_ROOTS, {
    fetchPolicy: "cache-and-network",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load Git roots", error);
      });
    },
    variables: {
      filters: {
        branches: branches ?? undefined,
        cloningStatuses:
          cloningStatuses === undefined
            ? undefined
            : (cloningStatuses as CloningStatus[]),

        includesHealthCheck,
        nicknames:
          nicknames === undefined ? undefined : (nicknames as string[]),
        search,
        state: state === undefined ? undefined : (state as ResourceState),
      },
      first,
      groupName,
      sortBy,
    },
  });

  const rootsResult = getFragmentData(GET_GIT_ROOTS_FRAGMENT, data);
  const gitRoots = useMemo(
    (): IGitRootAttr[] => getGitRootsFromResult(rootsResult),
    [rootsResult],
  );

  const fragmentData = useMemo(
    (): GetGitRootsViewFragmentFragment | undefined =>
      data as unknown as GetGitRootsViewFragmentFragment | undefined,
    [data],
  );

  const total = useMemo((): number => {
    return fragmentData?.group.gitRoots.total ?? 0;
  }, [fragmentData]);

  const endCursor = useMemo((): string => {
    return fragmentData?.group.gitRoots.pageInfo
      ? fragmentData.group.gitRoots.pageInfo.endCursor
      : "";
  }, [fragmentData]);

  return {
    gitRoots,
    handleNextPage: async (): Promise<void> => {
      await fetchMore({ variables: { after: endCursor } });
    },
    loadingRoots,
    rootsRefetch,
    total,
  };
};

export { useGitRootsDataQuery };
